var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { translate } from '@utils/i18n';
import api from '@utils/axios';
import Table, { TableSize } from '@components/Table';
import SearchPanel from '@components/SearchPanel';
import Select from '@components/v2/Select';
import Brushaway from '@components/Brushaway';
import { Loader, LoaderSizes } from '@components/Loader/Loader';
import { externalFormsPath } from '../../../../routes';
import { namespace, supportEmail } from './constants';
import { useExternalFormTemplates } from '../../FormLibrary/useTemplates';
import LocationDisplay from '../../FormLibrary/LocationDisplay';
import '../../FormLibrary/styles.scss';
function ImportForm({ isImportFormOpen, onClose, onFormImported }) {
    const [searchText, setSearchText] = useState('');
    const [selectedState, setSelectedState] = useState('');
    const { templates, loading, removeTemplate, allStates } = useExternalFormTemplates(searchText, selectedState);
    const handleImportTemplate = useCallback((template) => __awaiter(this, void 0, void 0, function* () {
        try {
            const res = yield api.post(externalFormsPath({ format: 'json', template_id: template.id }));
            const externalForm = res.data;
            onFormImported(externalForm);
            removeTemplate(template);
            toast.success(translate('import_component.success_import', { namespace }));
        }
        catch (error) {
            toast.error(translate('import_component.failed_import', { namespace }));
        }
    }), [onFormImported, removeTemplate]);
    const mailtoLink = useMemo(() => {
        const subject = translate('import_component.mailto.subject', { namespace });
        const body = translate('import_component.mailto.body', { namespace });
        const linkText = translate('import_component.mailto.link_text', { namespace });
        const mailto = `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        return (React.createElement("a", { href: mailto, target: "_blank", rel: "noopener noreferrer" }, linkText));
    }, []);
    const formTemplateTableColumns = useMemo(() => [
        {
            label: translate('headers.display_name', { namespace }),
            dataKey: 'displayName',
            renderer: ({ item }) => (React.createElement("div", null,
                item.pdfFileUrl ? (React.createElement("a", { href: item.pdfFileUrl, target: "_blank", rel: "noopener noreferrer" }, item.displayName)) : (React.createElement("span", null, item.displayName)),
                item.isPrivate && (React.createElement("span", { className: "form-badge--private" }, translate('import_component.private_form', { namespace })))))
        },
        {
            label: translate('headers.location', { namespace }),
            dataKey: 'location',
            renderer: ({ item }) => React.createElement(LocationDisplay, { city: item.city, state: item.state, country: item.country })
        },
        {
            label: '',
            dataKey: 'id',
            renderer: ({ item }) => (React.createElement("button", { type: "button", className: "qmb-control", onClick: () => handleImportTemplate(item) }, translate('import_component.import_button', { namespace })))
        }
    ], [handleImportTemplate]);
    return (React.createElement(Brushaway, { open: isImportFormOpen, title: translate('add_form', { namespace }), onClose: onClose },
        React.createElement("div", { className: "missing-form-message", dangerouslySetInnerHTML: {
                __html: translate('import_component.missing_form', {
                    namespace
                })
            } }),
        React.createElement("div", { className: "filters__group--search mb-3" },
            React.createElement(SearchPanel, { value: searchText, placeholder: translate('search', { namespace: 'filters' }), onChange: setSearchText }),
            React.createElement(Select, { label: translate('import_component.all_states', { namespace }), value: selectedState, options: [
                    { value: '', label: translate('import_component.all_states', { namespace }) },
                    ...allStates.map((state) => ({ value: state, label: state }))
                ], onChange: setSelectedState })),
        React.createElement("div", null, loading ? (React.createElement("div", { className: "loading-center" },
            React.createElement(Loader, { size: LoaderSizes.xlarge }))) : (React.createElement(Table, { columns: formTemplateTableColumns, data: templates, size: TableSize.Full, variant: "listing" })))));
}
export default ImportForm;
